/**
 * CompanyInfoEditor - Structured form for company_id node
 *
 * Provides a form with fields for company details:
 * - Company name, address, city, state, zip
 * - Website, LinkedIn URL
 * - Revenue tier
 * - Notes
 */

import { useState, useCallback } from 'react'
import { companyProfileApi, CompanyInfoData } from '@services/companyProfileApi'

export interface CompanyInfoEditorProps {
  value: string
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  companyId?: number
}

const REVENUE_TIERS = [
  '< $1 million',
  '$1 - $2 million',
  '$3 - $5 million',
  '$6 - 10 million',
  '$11 - $20 million',
  '> $20 million'
]

export function CompanyInfoEditor({
  value,
  onSave,
  onCancel,
  saving,
  companyId
}: CompanyInfoEditorProps) {
  // Parse initial value
  const initialData = companyProfileApi.parseCompanyInfo(value)

  const [formData, setFormData] = useState<CompanyInfoData>({
    company_name: initialData.company_name || '',
    street_address: initialData.street_address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip_code: initialData.zip_code || '',
    web_url: initialData.web_url || '',
    linkedin_url: initialData.linkedin_url || '',
    revenue_tier: initialData.revenue_tier || '',
    naics_code: initialData.naics_code || '',
    note: initialData.note || ''
  })

  const [naicsLabel, setNaicsLabel] = useState<string>('')
  const [derivingNaics, setDerivingNaics] = useState(false)

  const handleChange = useCallback((field: keyof CompanyInfoData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleSave = useCallback(() => {
    const serialized = companyProfileApi.serializeCompanyInfo(formData)
    onSave(serialized)
  }, [formData, onSave])

  const handleDeriveNaics = useCallback(async () => {
    if (!companyId) return
    setDerivingNaics(true)
    try {
      const result = await companyProfileApi.deriveNaics(companyId)
      if (result.naics_code) {
        setFormData(prev => ({ ...prev, naics_code: result.naics_code || '' }))
        setNaicsLabel(result.industry_label || '')
      }
    } catch (err) {
      console.error('NAICS derivation failed:', err)
    } finally {
      setDerivingNaics(false)
    }
  }, [companyId])

  const handleClear = useCallback(() => {
    setFormData({
      company_name: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      web_url: '',
      linkedin_url: '',
      revenue_tier: '',
      naics_code: '',
      note: ''
    })
    setNaicsLabel('')
  }, [])

  const hasData = Object.entries(formData).some(([key, val]) =>
    key !== '_format_version' && val && String(val).trim()
  )

  return (
    <div className="editor-form">
      <div className="editor-field">
        <label htmlFor="company-name">Company Name</label>
        <input
          id="company-name"
          type="text"
          value={formData.company_name || ''}
          onChange={(e) => handleChange('company_name', e.target.value)}
          placeholder="Enter company name"
        />
      </div>

      <div className="editor-field">
        <label htmlFor="street-address">Street Address</label>
        <input
          id="street-address"
          type="text"
          value={formData.street_address || ''}
          onChange={(e) => handleChange('street_address', e.target.value)}
          placeholder="Enter street address"
        />
      </div>

      <div className="editor-field-row">
        <div className="editor-field">
          <label htmlFor="city">City</label>
          <input
            id="city"
            type="text"
            value={formData.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
          />
        </div>
        <div className="editor-field">
          <label htmlFor="state">State</label>
          <input
            id="state"
            type="text"
            value={formData.state || ''}
            onChange={(e) => handleChange('state', e.target.value)}
            placeholder="State"
          />
        </div>
        <div className="editor-field">
          <label htmlFor="zip">ZIP Code</label>
          <input
            id="zip"
            type="text"
            value={formData.zip_code || ''}
            onChange={(e) => handleChange('zip_code', e.target.value)}
            placeholder="ZIP"
          />
        </div>
      </div>

      <div className="editor-field">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          type="url"
          value={formData.web_url || ''}
          onChange={(e) => handleChange('web_url', e.target.value)}
          placeholder="https://example.com"
        />
      </div>

      <div className="editor-field">
        <label htmlFor="linkedin">LinkedIn</label>
        <input
          id="linkedin"
          type="url"
          value={formData.linkedin_url || ''}
          onChange={(e) => handleChange('linkedin_url', e.target.value)}
          placeholder="https://www.linkedin.com/company/..."
        />
      </div>

      <div className="editor-field">
        <label htmlFor="revenue-tier">Revenue Tier</label>
        <select
          id="revenue-tier"
          value={formData.revenue_tier || ''}
          onChange={(e) => handleChange('revenue_tier', e.target.value)}
        >
          <option value="">Select revenue tier...</option>
          {REVENUE_TIERS.map(tier => (
            <option key={tier} value={tier}>{tier}</option>
          ))}
        </select>
      </div>

      <div className="editor-field">
        <label htmlFor="naics-code">NAICS Code</label>
        <div className="editor-field-row">
          <input
            id="naics-code"
            type="text"
            value={formData.naics_code || ''}
            onChange={(e) => handleChange('naics_code', e.target.value)}
            placeholder="e.g., 621340"
            style={{ flex: 1 }}
          />
          {companyId && (
            <button
              type="button"
              className="editor-btn editor-btn-secondary"
              onClick={handleDeriveNaics}
              disabled={derivingNaics || saving}
              style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
            >
              {derivingNaics ? 'Detecting...' : 'Auto-detect'}
            </button>
          )}
        </div>
        {naicsLabel && (
          <span className="editor-field-hint">{naicsLabel}</span>
        )}
      </div>

      <div className="editor-field">
        <label htmlFor="note">Additional Notes</label>
        <textarea
          id="note"
          value={formData.note || ''}
          onChange={(e) => handleChange('note', e.target.value)}
          placeholder="Any additional company information..."
          rows={4}
        />
      </div>

      <div className="editor-buttons">
        {hasData && (
          <button
            type="button"
            className="editor-btn editor-btn-secondary"
            onClick={handleClear}
            disabled={saving}
          >
            Clear All
          </button>
        )}
        <button
          type="button"
          className="editor-btn editor-btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="editor-btn editor-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}

export default CompanyInfoEditor
